import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  CalendarDays,
  RefreshCw,
  Play,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Clock,
  Package,
  ListFilter,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_OPTIONS = [
  { value: 'P1', label: 'P1 — Critical', color: 'bg-red-100 text-red-700' },
  { value: 'P2', label: 'P2 — High',     color: 'bg-orange-100 text-orange-700' },
  { value: 'P3', label: 'P3 — Medium',   color: 'bg-yellow-100 text-yellow-700' },
  { value: 'P4', label: 'P4 — Low',      color: 'bg-slate-100 text-slate-600' },
];

const STATUS_COLORS = {
  pending:  'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  ordered:  'bg-indigo-100 text-indigo-700',
  partial:  'bg-purple-100 text-purple-700',
  cancelled:'bg-red-100 text-red-700',
};

const REVIEW_STATUS_COLORS = {
  pending_review: 'bg-orange-100 text-orange-700',
  continued:      'bg-blue-100 text-blue-700',
  closed:         'bg-green-100 text-green-700',
};

function PriorityBadge({ priority }) {
  if (!priority) return null;
  const opt = PRIORITY_OPTIONS.find(o => o.value === priority);
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${opt?.color || 'bg-slate-100 text-slate-600'}`}>
      {priority}
    </span>
  );
}

function DeptSection({ dept, items, onDecision, onBulkDecision, loading }) {
  const [expanded, setExpanded] = useState(true);
  const [selected, setSelected] = useState([]);
  const [bulkPriority, setBulkPriority] = useState('');

  const toggle = (id) =>
    setSelected(s => s.includes(id) ? s.filter(i => i !== id) : [...s, id]);

  const toggleAll = () =>
    setSelected(selected.length === items.length ? [] : items.map(i => i.id));

  const handleBulk = (action) => {
    if (!action || selected.length === 0) return;
    onBulkDecision(selected, action, bulkPriority || null, '');
    setSelected([]);
  };

  const deptLabel = dept.charAt(0).toUpperCase() + dept.slice(1);

  return (
    <div className="border rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid={`dept-section-${dept}`}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
          <span className="font-semibold text-slate-800">{deptLabel} Department</span>
          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">{items.length} item{items.length !== 1 ? 's' : ''}</span>
        </div>
        <span className="text-xs text-slate-500">
          {items.filter(i => i.review_status === 'pending_review').length} pending
        </span>
      </button>

      {expanded && (
        <div>
          {/* Bulk Actions Bar */}
          {selected.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 border-b text-sm" data-testid="bulk-actions-bar">
              <span className="font-medium text-blue-700">{selected.length} selected</span>
              <Select value={bulkPriority} onValueChange={setBulkPriority}>
                <SelectTrigger className="h-7 w-36 text-xs">
                  <SelectValue placeholder="Set priority" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-blue-400 text-blue-700 hover:bg-blue-100"
                onClick={() => handleBulk('continue')}
                data-testid="bulk-continue-btn"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" /> Continue All
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-red-400 text-red-700 hover:bg-red-50"
                onClick={() => handleBulk('close')}
                data-testid="bulk-close-btn"
              >
                <XCircle className="h-3 w-3 mr-1" /> Close All
              </Button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      checked={selected.length === items.length && items.length > 0}
                      onChange={toggleAll}
                      className="rounded"
                      data-testid={`select-all-${dept}`}
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-slate-600 font-medium">Indent #</th>
                  <th className="px-3 py-2 text-left text-slate-600 font-medium">Part / Description</th>
                  <th className="px-3 py-2 text-left text-slate-600 font-medium">Status</th>
                  <th className="px-3 py-2 text-left text-slate-600 font-medium">Date</th>
                  <th className="px-3 py-2 text-left text-slate-600 font-medium">Review</th>
                  <th className="px-3 py-2 text-left text-slate-600 font-medium">Priority</th>
                  <th className="px-3 py-2 text-left text-slate-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((item) => (
                  <ReviewRow
                    key={item.id}
                    item={item}
                    selected={selected.includes(item.id)}
                    onToggle={() => toggle(item.id)}
                    onDecision={onDecision}
                    loading={loading}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewRow({ item, selected, onToggle, onDecision, loading }) {
  const [priority, setPriority] = useState(item.review_priority || '');
  const [submitting, setSubmitting] = useState(false);

  const parts = item.items || [];
  const partSummary = parts.length > 0
    ? parts.map(p => p.part_name || p.description || '—').slice(0, 2).join(', ') + (parts.length > 2 ? ` +${parts.length - 2} more` : '')
    : [item.part_name, item.door_no].filter(Boolean).join(' — ') || '—';

  const handleAction = async (action) => {
    setSubmitting(true);
    await onDecision(item.id, action, priority || null, '');
    setSubmitting(false);
  };

  const isDone = item.review_status === 'continued' || item.review_status === 'closed';

  return (
    <tr
      className={`hover:bg-slate-50 transition-colors ${isDone ? 'opacity-60' : ''}`}
      data-testid={`review-row-${item.id}`}
    >
      <td className="px-3 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="rounded"
          disabled={isDone}
        />
      </td>
      <td className="px-3 py-2 font-mono text-xs text-blue-600 whitespace-nowrap">
        {item.pr_number || item.id?.slice(-6)?.toUpperCase()}
      </td>
      <td className="px-3 py-2 max-w-[200px]">
        <span className="line-clamp-2 text-slate-700">{partSummary}</span>
      </td>
      <td className="px-3 py-2">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[item.status] || 'bg-slate-100 text-slate-600'}`}>
          {item.status}
        </span>
      </td>
      <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">
        {item.created_at ? item.created_at.slice(0, 10) : '—'}
      </td>
      <td className="px-3 py-2">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${REVIEW_STATUS_COLORS[item.review_status] || 'bg-slate-100 text-slate-600'}`}>
          {item.review_status?.replace('_', ' ')}
        </span>
      </td>
      <td className="px-3 py-2">
        {isDone ? (
          <PriorityBadge priority={item.review_priority} />
        ) : (
          <Select value={priority} onValueChange={setPriority} disabled={submitting || loading}>
            <SelectTrigger className="h-7 w-24 text-xs" data-testid={`priority-select-${item.id}`}>
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="px-3 py-2">
        {isDone ? (
          <span className="text-xs text-slate-400">
            {item.review_status === 'closed' ? 'Closed' : 'Continued'} by {item.review_decision_by?.split('@')[0]}
          </span>
        ) : (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-blue-400 text-blue-700 hover:bg-blue-50 px-2"
              onClick={() => handleAction('continue')}
              disabled={submitting || loading}
              data-testid={`continue-btn-${item.id}`}
            >
              <CheckCircle2 className="h-3 w-3 mr-1" />Continue
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-red-400 text-red-700 hover:bg-red-50 px-2"
              onClick={() => handleAction('close')}
              disabled={submitting || loading}
              data-testid={`close-btn-${item.id}`}
            >
              <XCircle className="h-3 w-3 mr-1" />Close
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function MonthlyReviewPage({ user }) {
  const [reviewStatus, setReviewStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [activeTab, setActiveTab] = useState('all');    // 'all' | 'indents' | 'partial'
  const [filterStatus, setFilterStatus] = useState('');  // '' | 'pending_review' | 'continued' | 'closed'
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [currentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/monthly-review/status`, { headers });
      setReviewStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch review status', err);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.append('item_type', activeTab);
      if (filterStatus) params.append('review_status', filterStatus);
      const res = await axios.get(`${API}/api/monthly-review/items?${params}`, { headers });
      setItems(res.data.items || []);
      setGrouped(res.data.by_department || {});
    } catch (err) {
      console.error('Failed to fetch review items', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filterStatus]);

  useEffect(() => {
    fetchStatus();
    fetchItems();
  }, [fetchStatus, fetchItems]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const res = await axios.post(`${API}/api/monthly-review/trigger`, {}, { headers });
      toast.success(res.data.message);
      fetchStatus();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trigger review');
    } finally {
      setTriggering(false);
    }
  };

  const handleDecision = async (prId, action, priority, notes) => {
    try {
      await axios.post(
        `${API}/api/monthly-review/items/${prId}/decision`,
        { action, priority, notes },
        { headers }
      );
      toast.success(action === 'close' ? 'Item closed' : 'Item marked to continue');
      fetchItems();
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit decision');
    }
  };

  const handleBulkDecision = async (prIds, action, priority, notes) => {
    try {
      const res = await axios.post(
        `${API}/api/monthly-review/bulk-decision`,
        { pr_ids: prIds, action, priority, notes },
        { headers }
      );
      toast.success(`${res.data.succeeded}/${res.data.total} items updated`);
      fetchItems();
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    }
  };

  const review = reviewStatus?.review;
  const isNotTriggered = reviewStatus?.status === 'not_triggered';
  const isActive = reviewStatus?.status === 'active' || review?.status === 'active';

  const pendingCount = review?.pending_count ?? 0;
  const reviewedCount = review?.reviewed_count ?? 0;
  const totalItems = items.length;
  const indentsCount = items.filter(i => ['pending', 'approved'].includes(i.status)).length;
  const partialCount = items.filter(i => ['ordered', 'partial'].includes(i.status)).length;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-indigo-600" />
            Monthly Review
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Review open purchase indents and partially delivered orders for {currentMonth}
          </p>
        </div>
        {(user?.is_admin || user?.is_super_admin) && (
          <Button
            onClick={handleTrigger}
            disabled={triggering}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
            data-testid="trigger-review-btn"
          >
            {triggering ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {isActive ? 'Re-trigger Review' : 'Trigger Review'}
          </Button>
        )}
      </div>

      {/* Review Status Card */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Review Status</p>
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-green-500' : isNotTriggered ? 'bg-slate-300' : 'bg-yellow-400'}`} />
            <span className="font-semibold text-slate-800 capitalize">
              {isNotTriggered ? 'Not Triggered' : reviewStatus?.status?.replace('_', ' ')}
            </span>
          </div>
        </div>
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Total Items</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="total-items-count">{totalItems}</p>
        </div>
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Pending Review</p>
          <p className="text-2xl font-bold text-orange-600" data-testid="pending-count">{pendingCount}</p>
        </div>
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Reviewed</p>
          <p className="text-2xl font-bold text-green-600" data-testid="reviewed-count">{reviewedCount}</p>
        </div>
      </div>

      {isNotTriggered && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">No active review for {currentMonth}</p>
            <p className="text-sm text-amber-700 mt-1">
              Click "Trigger Review" to start the end-of-month review. This will flag all open indents and
              partial deliveries for department heads to review.
            </p>
          </div>
        </div>
      )}

      {/* Triggered info */}
      {review && (
        <div className="bg-slate-50 border rounded-lg px-4 py-3 flex items-center gap-4 text-sm text-slate-600 flex-wrap">
          <span><span className="font-medium">Month:</span> {review.month}</span>
          <span><span className="font-medium">Triggered by:</span> {review.triggered_by}</span>
          {review.triggered_at && (
            <span><span className="font-medium">On:</span> {new Date(review.triggered_at).toLocaleString('en-IN')}</span>
          )}
          <span><span className="font-medium">Indents flagged:</span> {review.indent_items_total}</span>
          <span><span className="font-medium">Partial POs flagged:</span> {review.partial_items_total}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Type tabs */}
        <div className="flex rounded-lg border overflow-hidden shadow-sm">
          {[
            { key: 'all',     label: `All (${totalItems})` },
            { key: 'indents', label: `Open Indents (${indentsCount})` },
            { key: 'partial', label: `Partial POs (${partialCount})` },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Review Status Filter */}
        <div className="flex items-center gap-2">
          <ListFilter className="h-4 w-4 text-slate-400" />
          <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-9 w-44 text-sm" data-testid="review-status-filter">
              <SelectValue placeholder="Filter by review status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All review statuses</SelectItem>
              <SelectItem value="pending_review">Pending Review</SelectItem>
              <SelectItem value="continued">Continued</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => { fetchItems(); fetchStatus(); }}
          className="text-slate-500"
          data-testid="refresh-btn"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Items */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-lg border shadow-sm p-12 text-center">
          {isNotTriggered ? (
            <>
              <CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">Trigger the review to load items</p>
            </>
          ) : (
            <>
              <CheckCircle2 className="h-12 w-12 text-green-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No items matching current filters</p>
            </>
          )}
        </div>
      ) : (
        <div>
          {Object.entries(grouped).map(([dept, deptItems]) => (
            <DeptSection
              key={dept}
              dept={dept}
              items={deptItems}
              onDecision={handleDecision}
              onBulkDecision={handleBulkDecision}
              loading={loading}
            />
          ))}
        </div>
      )}
    </div>
  );
}
